import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["container", "avatars"]
  static values = {
    heartbeatInterval: { type: Number, default: 15000 },
    currentAccountId: Number,
  }

  connect() {
    this.users = []
    this.subscription = null
    this.heartbeatTimer = null

    this.createSubscription()
    this.startHeartbeat()

    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.boundHandleUnload = this.handleUnload.bind(this)

    document.addEventListener("visibilitychange", this.boundHandleVisibilityChange)
    window.addEventListener("beforeunload", this.boundHandleUnload)
  }

  disconnect() {
    this.cleanup()
    document.removeEventListener("visibilitychange", this.boundHandleVisibilityChange)
    window.removeEventListener("beforeunload", this.boundHandleUnload)
  }

  createSubscription() {
    this.subscription = consumer.subscriptions.create(
      { channel: "PlantafelPresenceChannel" },
      {
        connected: () => {},
        disconnected: () => {
          this.users = []
          this.render()
        },
        received: (data) => {
          this.handleMessage(data)
        },
      },
    )
  }

  handleMessage(data) {
    if (data.type === "presence_update") {
      // Filter out current user client-side
      const allUsers = data.users || []
      this.users = allUsers.filter((user) => user.account_id !== this.currentAccountIdValue)
      this.render()
    }
  }

  startHeartbeat() {
    this.heartbeatTimer = setInterval(() => {
      if (this.subscription && document.visibilityState === "visible") {
        this.subscription.perform("heartbeat")
      }
    }, this.heartbeatIntervalValue)
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  handleVisibilityChange() {
    if (document.visibilityState === "visible") {
      if (!this.subscription) {
        this.createSubscription()
      }
      this.startHeartbeat()
    } else {
      this.stopHeartbeat()
    }
  }

  handleUnload() {
    this.cleanup()
  }

  cleanup() {
    this.stopHeartbeat()
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  render() {
    const count = this.users.length

    if (count === 0) {
      if (this.hasContainerTarget) {
        this.containerTarget.classList.add("hidden")
      }
      return
    }

    if (this.hasContainerTarget) {
      this.containerTarget.classList.remove("hidden")
    }

    if (this.hasAvatarsTarget) {
      this.renderAvatars()
    }
  }

  renderAvatars() {
    const maxVisible = 3
    const visible = this.users.slice(0, maxVisible)
    const overflow = this.users.length - maxVisible

    let html = visible
      .map(
        (user, index) => `
      <div class="relative -ml-2 first:ml-0" style="z-index: ${maxVisible - index}">
        <span
          class="inline-flex items-center justify-center h-7 w-7 rounded-full ring-2 ring-white text-xs font-medium text-white cursor-default"
          style="background-color: ${this.escapeHtml(user.color)}"
          title="${this.escapeHtml(user.display_name)}"
        >
          ${this.escapeHtml(user.initials)}
        </span>
        <span class="absolute bottom-0 right-0 block h-2 w-2 rounded-full bg-green-500 ring-1 ring-white"></span>
      </div>
    `,
      )
      .join("")

    if (overflow > 0) {
      const overflowTitle = this.users
        .slice(maxVisible)
        .map((u) => u.display_name)
        .join(", ")
      html += `
        <div class="relative -ml-2" style="z-index: 0">
          <span
            class="inline-flex items-center justify-center h-7 w-7 rounded-full ring-2 ring-white text-xs font-medium text-gray-600 bg-gray-200 cursor-default"
            title="${this.escapeHtml(overflowTitle)}"
          >
            +${overflow}
          </span>
        </div>
      `
    }

    this.avatarsTarget.innerHTML = html
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
